import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { getInvestQueryKey, useInvestDataState } from './_investDataState'

import {
  addManualBankAccount,
  getBankAccounts,
  getPlaidToken,
  getWireInstructions,
  updateBankAccount,
} from '../shared/_api'
import { InvestmentBankAccountTypes } from '../shared/_types'

type InvestQueryData = {
  investment?: {
    funding_status?: string
  } & Record<string, unknown>
} & Record<string, unknown>

type FundingStatusResponse = {
  funding_status?: string
}

export const useBankAccountsDataState = () => {
  const { data: investData } = useInvestDataState()

  return useQuery({
    queryKey: ['bankAccounts'],
    queryFn: async () => {
      const data = await getBankAccounts(investData?.investment?.id)
      const wireInstructions = await getWireInstructions(
        investData?.investment?.id
      )
      return {
        ...data,
        ...wireInstructions,
      }
    },
    enabled:
      !!investData.investment?.investment_account &&
      !location.pathname.includes('start'),
    staleTime: Infinity,
  })
}

export const useMutatePlaidToken = () => {
  const { data: investData } = useInvestDataState()
  const queryClient = useQueryClient()

  return useMutation({
    mutationKey: ['bankAccounts'],
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await getPlaidToken(investData?.investment?.id, data)
      return res
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['bankAccounts'])
    },
  })
}

export const useMutateManualACH = () => {
  const { data: investData } = useInvestDataState()
  const queryClient = useQueryClient()
  const investQueryKey = getInvestQueryKey()

  return useMutation({
    mutationFn: async (data: Record<string, unknown>) => {
      const res = await addManualBankAccount(investData?.investment?.id, data)
      return res
    },
    onSuccess: (data: FundingStatusResponse) => {
      console.log(data)
      queryClient.setQueryData<InvestQueryData | undefined>(
        investQueryKey,
        (oldData) =>
          oldData && {
            ...oldData,
            investment: {
              ...(oldData.investment ?? {}),
              funding_status: data.funding_status,
            },
          }
      )
    },
    onSettled: () => {
      queryClient.invalidateQueries(['invest'])
      queryClient.invalidateQueries(['bankAccounts'])
    },
  })
}

export const useMutateUpdateWithBankAccount = () => {
  const { data: investData } = useInvestDataState()
  const queryClient = useQueryClient()
  const investQueryKey = getInvestQueryKey()

  return useMutation({
    mutationFn: (data: InvestmentBankAccountTypes) =>
      updateBankAccount(investData?.investment?.id, data),
    onMutate: async (data: InvestmentBankAccountTypes) => {
      const previousInvestmentData =
        queryClient.getQueryData<InvestQueryData>(investQueryKey)
      queryClient.setQueryData<InvestQueryData | undefined>(
        investQueryKey,
        (oldData) =>
          oldData && {
            ...oldData,
            investment: {
              ...(oldData.investment ?? {}),
              funding_status: data.funding_status,
            },
          }
      )
      return { previousInvestmentData }
    },
    onError: (_err, _newData, context) => {
      queryClient.setQueryData(investQueryKey, context?.previousInvestmentData)
    },
    onSettled: () => {
      queryClient.invalidateQueries(['invest'])
    },
  })
}
